import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { uploadPhoto, deletePhoto, uploadPhotoBatch } from '../services/supabase/storage';
import { validateImageFile } from '../utils/imageCompression';

/**
 * Custom hook for image upload functionality
 * @returns {Object} - Upload methods and state
 */
export function useImageUpload() {
  const { user } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);

  /**
   * Upload single image
   * @param {File} file - Image file
   * @param {Object} metadata - Inspection metadata
   * @returns {Promise<Object>} - Upload result
   */
  const upload = async (file, metadata) => {
    setUploading(true);
    setError(null);
    setProgress(0);

    try {
      // Validate file
      const validation = validateImageFile(file);
      if (!validation.valid) {
        throw new Error(validation.error);
      }

      // Add user ID to metadata
      const fullMetadata = {
        ...metadata,
        userId: user.id,
      };

      // Simulate progress (actual progress not available with Supabase)
      setProgress(30);

      const result = await uploadPhoto(file, fullMetadata);

      if (result.error) {
        throw new Error(result.error);
      }

      setProgress(100);
      return result;
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.message);
      return { path: null, url: null, error: err.message };
    } finally {
      setUploading(false);
      setTimeout(() => setProgress(0), 1000);
    }
  };

  /**
   * Upload multiple images
   * @param {File[]} files - Array of image files
   * @param {Object} metadata - Inspection metadata
   * @returns {Promise<Object>} - Batch upload results
   */
  const uploadMultiple = async (files, metadata) => {
    setUploading(true);
    setError(null);
    setProgress(0);

    try {
      // Validate all files
      for (const file of files) {
        const validation = validateImageFile(file);
        if (!validation.valid) {
          throw new Error(`${file.name}: ${validation.error}`);
        }
      }

      const fullMetadata = {
        ...metadata,
        userId: user.id,
      };

      // Upload with progress tracking
      const results = [];
      for (let i = 0; i < files.length; i++) {
        const result = await uploadPhoto(files[i], fullMetadata);
        results.push(result);
        setProgress(Math.round(((i + 1) / files.length) * 100));
      }

      const errorCount = results.filter(r => r.error).length;
      if (errorCount > 0) {
        throw new Error(`${errorCount} z ${files.length} zdjęć nie zostało przesłanych`);
      }

      return { results, successCount: results.length, errorCount: 0 };
    } catch (err) {
      console.error('Batch upload error:', err);
      setError(err.message);
      return { results: [], successCount: 0, errorCount: files.length, error: err.message };
    } finally {
      setUploading(false);
      setTimeout(() => setProgress(0), 1000);
    }
  };

  /**
   * Delete image
   * @param {string} path - Storage path
   * @returns {Promise<Object>} - Delete result
   */
  const remove = async (path) => {
    try {
      const result = await deletePhoto(path);
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    } catch (err) {
      console.error('Delete error:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  /**
   * Reset error state
   */
  const clearError = () => {
    setError(null);
  };

  return {
    upload,
    uploadMultiple,
    remove,
    uploading,
    progress,
    error,
    clearError,
  };
}
